import React, { createContext, useState, useEffect } from 'react'
import {
  listWorkspacesByClientId,
  listWorkspaceSubcategories,
  listFilesByClient
} from '../services/workspaces'
import { listClientsByParent, getClientDataById } from '../services/clients'
import { listClientDocuments } from '../services/documents'
import _ from 'lodash'
import { authContext } from './AuthContext'
import { useContext } from 'react'

export const clientContext = createContext({})

const ClientProvider = ({ children }) => {
  const { auth } = useContext(authContext)

  const [id, setId] = useState(-1)
  const [data, setData] = useState({
    id: -1,
    name: '',
    license: '',
    type: '',
    workspaces: [],
    companies: []
  })
  const [workspaces, setWorkspaces] = useState({})
  const [selectionChain, setSelectionChain] = useState([])
  const [currentViewerDoc, setCurrentViewerDoc] = useState({})
  const [dataReady, setDataReady] = useState(false)

  const initContext = async () => {
    if (id === -1) return
    setDataReady(false)

    let clientData = await getClientDataById(id)
    let data = {
      id: clientData.id,
      name: clientData.name,
      license: clientData.license,
      type: clientData.type
    }

    let workspaces = await listWorkspacesByClientId(id)
    await Promise.all(
      workspaces.map(async (workspace) => {
        let list = await listWorkspaceSubcategories(workspace.workspace_id)
        workspace.subcategories = list
      })
    )

    try {
      data.workspaces = JSON.parse(JSON.stringify(workspaces))

      // Files must exist before docs are assigned to them
      await getClientFiles(id, data.workspaces)
      await getDocuments(id, data.workspaces)

      let companies = await listClientsByParent(id)
      // Promise.all so companies load in parallel, but files before docs within each
      await Promise.all(
        companies.map(async (c) => {
          c.workspaces = JSON.parse(JSON.stringify(workspaces))
          await getClientFiles(c.id, c.workspaces) // files first
          await getDocuments(c.id, c.workspaces) // then docs
        })
      )
      data.companies = companies
    } catch (e) {
      console.log('Error', e)
    }

    setWorkspaces(workspaces)
    setData(data)
    setDataReady(true)
  }

  const getDocuments = async (clientId, clientWorkspaces) => {
    let documents = await listClientDocuments(
      clientId,
      auth.type === 'blp' ? '1' : '0'
    )
    documents.forEach((d) => {
      let ws = _.find(clientWorkspaces, (w) => {
        return w.name_en === d.category_en && w.name_es === d.category_es
      })
      if (ws) {
        let wsSubcategories = ws.subcategories
        let dSubcategory = _.find(wsSubcategories, (s) => {
          return (
            s.subcategory_en === d.subcategory_en &&
            s.subcategory_es === d.subcategory_es
          )
        })
        try {
          if (d.file_id === null) {
            //document directly in subcategory
            if (!dSubcategory.docs) dSubcategory.docs = []
            dSubcategory.docs.push(d)
          } else {
            //document inside specific file
            let dFile = _.find(dSubcategory.files, (f) => {
              return f.id === d.file_id
            })
            if (dFile) {
              if (!dFile.docs) dFile.docs = []
              dFile.docs.push(d)
            }
          }
        } catch (e) {
          console.log('Error')
        }
      }
    })
  }

  const getClientFiles = async (clientId, clientWorkspaces) => {
    let files = await listFilesByClient(clientId)
    files.forEach((f) => {
      let ws = _.find(clientWorkspaces, (w) => {
        return w.workspace_id === f.category_id
      })

      if (!ws || !ws.subcategories) return
      let fSubcategory = []
      fSubcategory = _.find(ws.subcategories, (s) => {
        return s.id === f.subcategory_id
      })

      if (!fSubcategory) return
      if (!fSubcategory.files) fSubcategory.files = []
      fSubcategory.files.push(f)
    })
  }

  const refreshDeletedDoc = async (docId) => {
    _.forEach(data.workspaces, (w) => {
      _.forEach(w.subcategories, (s) => {
        _.forEach(s.files, (f) => {
          let idx = _.findIndex(f.docs, (d) => d.id === docId)
          if (idx > -1) {
            _.remove(f.docs, (d) => d.id === docId)
            return
          }
        })

        let idx = _.findIndex(s.docs, (d) => d.id === docId)
        if (idx > -1) {
          _.remove(s.docs, (d) => d.id === docId)
          return
        }
      })
    })

    _.forEach(data.companies, (c) => {
      _.forEach(c.workspaces, (w) => {
        _.forEach(w.subcategories, (s) => {
          _.forEach(s.files, (f) => {
            let idx = _.findIndex(f.docs, (d) => d.id === docId)
            if (idx > -1) {
              _.remove(f.docs, (d) => d.id === docId)
              return
            }
          })

          let idx = _.findIndex(s.docs, (d) => d.id === docId)
          if (idx > -1) {
            _.remove(s.docs, (d) => d.id === docId)
            return
          }
        })
      })
    })

    setData({ ...data })
  }

  const refreshUpdatedDoc = async (doc) => {
    _.forEach(data.workspaces, (w) => {
      _.forEach(w.subcategories, (s) => {
        _.forEach(s.files, (f) => {
          let idx = _.findIndex(f.docs, (d) => d.id === doc.id)
          if (idx > -1) {
            f.docs.splice(idx, 1, doc)
            return
          }
        })

        let idx = _.findIndex(s.docs, (d) => d.id === doc.id)
        if (idx > -1) {
          s.docs.splice(idx, 1, doc)
          return
        }
      })
    })

    _.forEach(data.companies, (c) => {
      _.forEach(c.workspaces, (w) => {
        _.forEach(w.subcategories, (s) => {
          _.forEach(s.files, (f) => {
            let idx = _.findIndex(f.docs, (d) => d.id === doc.id)
            if (idx > -1) {
              f.docs.splice(idx, 1, doc)
              return
            }
          })

          let idx = _.findIndex(s.docs, (d) => d.id === doc.id)
          if (idx > -1) {
            s.docs.splice(idx, 1, doc)
            return
          }
        })
      })
    })

    setData({ ...data })
  }

  const refreshAddedDoc = async () => {
    await initContext()
  }

  useEffect(() => {
    setData({
      id: -1,
      name: '',
      license: '',
      type: '',
      workspaces: [],
      companies: []
    })
    setDataReady(false)
    initContext()
  }, [id])

  return (
    <clientContext.Provider
      value={{
        id,
        setId,
        data,
        setData,
        workspaces,
        selectionChain,
        setSelectionChain,
        currentViewerDoc,
        setCurrentViewerDoc,
        refreshDeletedDoc,
        refreshUpdatedDoc,
        refreshAddedDoc,
        dataReady
      }}
    >
      {children}
    </clientContext.Provider>
  )
}

export default ClientProvider
